import React, { FC, useEffect, useMemo, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import {
  Box,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  VStack,
} from '@chakra-ui/react';

import { InternationalTransferDynamicFormField } from './components/InternationalTransferDynamicFormField';
import { InternationalTransferDynamicFormConfig } from './types';
import { filterEmptyFields } from './utils/filterEmptyFields';

interface InternationalTransferDynamicFormProps {
  configs: InternationalTransferDynamicFormConfig[];
  /**
   * Trigger the form submission with a submit button outside the form
   * @example
   * ```tsx
   * <InternationalTransferDynamicForm id="formId" />
   * <Button form="formId" type="submit">
   * ```
   */
  id: string;
  onRefresh: (values: FieldValues) => void;
  onSubmit: (type: string, values: FieldValues) => void;
}

export const InternationalTransferDynamicForm: FC<
  InternationalTransferDynamicFormProps
> = ({ configs, id, onRefresh, onSubmit }) => {
  const form = useForm({ shouldUnregister: true });
  const [formConfigIndex, setFormConfigIndex] = useState(0);

  const currentConfigFieldsFlattened = useMemo(
    () => configs[formConfigIndex]?.fields.flatMap(({ group }) => group) ?? [],
    [configs, formConfigIndex],
  );

  const configType = configs[formConfigIndex]?.type as string;

  useEffect(() => {
    const subscription = form.watch((values, { name, type }) => {
      if (type !== 'change') {
        return;
      }

      if (
        !currentConfigFieldsFlattened.some(
          ({ key, refreshRequirementsOnChange }) =>
            key === name && refreshRequirementsOnChange,
        )
      ) {
        return;
      }

      const cleanedValues = filterEmptyFields(values);
      if (!cleanedValues) {
        return;
      }

      onRefresh(cleanedValues);
    });

    return subscription.unsubscribe;
  }, [currentConfigFieldsFlattened, form, onRefresh, configType]);

  const handleTabsChange = (index: number) => {
    setFormConfigIndex(index);
  };

  const handleSubmit = form.handleSubmit((values) => {
    const cleanedValues = filterEmptyFields(values);
    if (!cleanedValues) {
      return;
    }

    onSubmit(configType, cleanedValues);
  });

  return (
    <FormProvider {...form}>
      <form id={id} onSubmit={handleSubmit}>
        <Tabs
          index={formConfigIndex}
          isLazy
          onChange={handleTabsChange}
          variant="unstyled"
        >
          {configs.length > 1 ? (
            <TabList gap="space-16" paddingY="space-32">
              {configs.map((config) => (
                <Tab
                  _selected={{ borderColor: 'grey.800' }}
                  borderColor="grey.400"
                  borderRadius="radius-6"
                  borderStyle="solid"
                  borderWidth={1}
                  color="secondary"
                  height="space-32"
                  key={config.type}
                  paddingX="space-16"
                  paddingY="space-6"
                >
                  {config.title}
                </Tab>
              ))}
            </TabList>
          ) : (
            <Box height="space-32" />
          )}
          <TabPanels>
            {configs.map((config) => (
              <TabPanel key={config.type}>
                <VStack alignItems="stretch" spacing="space-20">
                  {config.fields.map(({ group }) =>
                    group.map(({ key: name, name: label, ...field }) => (
                      <InternationalTransferDynamicFormField
                        key={name}
                        label={label}
                        name={name}
                        {...field}
                      />
                    )),
                  )}
                </VStack>
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </form>
    </FormProvider>
  );
};
